import { ActionContext, Module } from 'vuex';
import { AuthUser, Company } from '../models';
import { CompanyState, RootState } from '../store/state_models';

import defaults from '@/config';
import firebase from 'firebase/compat/app';
import { getValidUrl } from '@/util/helper';
import moment from 'moment-timezone';

let unsubscribe: () => void;
const companyDateFields = [
	'dateupdated',
	'dateadded',
	'dateinactivation',
	'termsofserviceaccepteddate',
	'privacypolicyaccepteddate',
	'affiliatepolicyaccepteddate',
	'upgradealertdismissed'
]

export const company: Module<CompanyState, RootState> = {
	namespaced: true,
	state: {},
	getters: {
		get: (state: CompanyState) => {
			return state.company;
    },
    inSaasPlan: (state: CompanyState) => {
      const saasPlans = ['agency_monthly_497', 'agency_annual_497']
      const companyPlan = state.company.stripe_active_plan
      return state.company.is_enterprise || saasPlans.includes(companyPlan);
    },
	in297Plan:(state: CompanyState)=>{
		const saasPlans = ['agency_monthly_297','agency_annual_297','agency_monthly_297_manual', 'agency_monthly_237_LVRG']
		const companyPlan = state.company.stripe_active_plan
		return saasPlans.includes(companyPlan);
	},
	isAbove97Plan: (state: CompanyState) => {
			const companyPlan = state.company.stripe_active_plan
			const stripeActivePlanAmount = companyPlan?.split('_')[2]
			if (!stripeActivePlanAmount || stripeActivePlanAmount === '97') return false
			return true
	}
	},
	mutations: {
		set: (state: CompanyState, payload: object) => {
			state.company = payload
		},
		setWhiteLabelDomain: (state: CompanyState, payload: any) => {
			state.whiteLabelUrl = payload.url
			state.isWhiteLabelled = payload.isWhiteLabelled
		}
	},
	actions: {
		get: {
			handler: (context: ActionContext<CompanyState, RootState>): Promise<Company> => {
				return new Promise(async (resolve, reject) => {
					if (context.state.company) {
						return resolve(new Company(context.state.company));
					}

					try {
						const credentials = new AuthUser(await context.dispatch('auth/get', null, { root: true }));
						const company = await Company.getById(credentials.companyId);
						context.commit('set', { ...company.secureData, id: company.id });
						resolve(company);
					} catch {
						reject();
					}
				});
			},
		},
		update: {
			handler: (context: ActionContext<CompanyState, RootState>, payload: object): Promise<Company | undefined> => {
				return new Promise(async (resolve, reject) => {
					try {
						const company = new Company(JSON.parse(JSON.stringify(context.state.company)))
						const payloadKeys = Object.keys(payload)
						for (const key of payloadKeys) {
							if (key !== 'id' && key !== 'dateAdded') {
								let val = payload[key]
								if (companyDateFields.includes(key.toLowerCase())) {
									val = moment(val)
								}
								company[key] = val
							}
						}
						context.commit('set', { ...company.secureData, id: company.id });
						resolve(company);
					} catch (e) {
						reject(e);
					}
				});
			},
		},
		getWhitelabelDomain: {
			handler: (context: ActionContext<CompanyState, RootState>): Promise<string> => {
				return new Promise(async (resolve) => {
					if (context.state.whiteLabelUrl) {
						resolve(context.state.whiteLabelUrl);
					} else {
						let stateCompany;
						if (context.state.company) {
							stateCompany = new Company(context.state.company);
						} else {
							const credentials = new AuthUser(await context.dispatch('auth/get', null, { root: true }));
							stateCompany = await Company.getById(credentials.companyId);
							context.commit('set', { ...stateCompany.secureData, id: stateCompany.id });
						}
						let whiteLabelDomain;
						if (stateCompany.sparedomain) {
							whiteLabelDomain = stateCompany.sparedomain;
						}
						if (!whiteLabelDomain) {
							whiteLabelDomain = defaults.widgetUrl;
						}
						context.commit('setWhiteLabelDomain', { url: getValidUrl(whiteLabelDomain), isWhiteLabelled: Boolean(stateCompany.sparedomain)});
						resolve(context.state.whiteLabelUrl);
					}
				});
			},
		},
		syncAll: {
			root: true,
			handler: async (context: ActionContext<CompanyState, RootState>) => {
				let credentials: AuthUser;
				try {
					credentials = new AuthUser(await context.dispatch('auth/get', null, { root: true }));
				} catch {
					return;
				}
				if (unsubscribe) unsubscribe()
				unsubscribe = Company.getStreamById(credentials.companyId).onSnapshot((snapshot: firebase.firestore.DocumentSnapshot) => {

          		const company = new Company(snapshot.data());
					context.commit('set', { ...company.secureData, id: snapshot.id });
				}, err => {
          console.error(err)
          throw err
        });
			},
		},
		logout: {
			root: true,
			handler: async () => {
				if (unsubscribe) {
					unsubscribe();
				}
			},
		},
	},
};
